import { type AuditorTrail } from "../../../../models/audit/attributeTestColumn";
import { type Citation } from "../../../../models/audit/citation";

/**
 * One displayed evidence item in the auditor trail: a single piece of evidence
 * that may have two views — the live source document (`source`, e.g. the xlsx)
 * and its rendered `screenshot`. A screenshot citation inherits its source's
 * `encodedCitationString`, so the two collapse to one row sharing one tickmark
 * `code`. `screenshot` is null when no screenshot sibling exists yet (e.g. before
 * a workpaper generation).
 */
export interface TrailDisplayItem {
  code: string;
  source: Citation;
  screenshot: Citation | null;
}

export interface TrailDisplayStep {
  stepIndex: number;
  items: TrailDisplayItem[];
}

/**
 * Collapse each step's citations into display items and assign tickmark codes,
 * mirroring the backend `auditor_trail_tickmark_codes` exactly. A screenshot
 * citation inherits its source's `encodedCitationString` but lives on a DIFFERENT
 * document, so within a step a later citation sharing an encoded string AND on a
 * different document is that screenshot and folds into the source's item (one
 * code). Two citations sharing an encoded string on the SAME document are distinct
 * claims (e.g. two annotations on one PDF element) and each get their own item +
 * code — only true source/screenshot pairs collapse. Numbering runs continuously
 * across steps; the citation number always joins the column code with a dot
 * ("A1.1", "B.1").
 *
 * `isScreenshotDoc` only picks which member of a collapsed pair is the screenshot
 * view; the live-document member stays the canonical `source`.
 */
export function buildTrailDisplay(
  trail: AuditorTrail,
  attributeCode: string | undefined,
  isScreenshotDoc: (documentId: string | null | undefined) => boolean,
): TrailDisplayStep[] {
  // The attribute-test column code already disambiguates shared attributes
  // ("A1"/"A2"), so the citation number always joins with a dot ("A1.1", "B.1").
  const separator = attributeCode ? "." : "";
  let runningNumber = 0;

  return trail.steps.map((step, stepIndex) => {
    const items: TrailDisplayItem[] = [];
    // encodedCitationString -> the source item it owns, so a screenshot (same
    // encoded string, different document) folds in instead of starting a new
    // tickmark.
    const itemByEncoded = new Map<string, { doc: string; index: number }>();

    for (const citation of step.citations) {
      const key = citation.encodedCitationString || citation.id || "";
      const primary = itemByEncoded.get(key);
      if (primary && primary.doc !== citation.documentId) {
        // Screenshot view of an existing source: keep the live document as
        // `source` and the screenshot document as `screenshot`.
        const item = items[primary.index];
        if (isScreenshotDoc(item.source.documentId)) {
          item.screenshot = item.source;
          item.source = citation;
        } else if (!item.screenshot) {
          item.screenshot = citation;
        }
      } else {
        runningNumber += 1;
        const code = attributeCode
          ? `${attributeCode}${separator}${runningNumber}`
          : `${runningNumber}`;
        items.push({ code, source: citation, screenshot: null });
        if (!primary) {
          itemByEncoded.set(key, {
            doc: citation.documentId,
            index: items.length - 1,
          });
        }
      }
    }

    return { stepIndex, items };
  });
}

/**
 * Find the display item (source + screenshot pair) that owns `citationId`, for
 * driving the doc-viewer's screenshot/source toggle. Returns null when there's
 * no trail, no id, or no match.
 */
export function findTrailDisplayItem(
  trail: AuditorTrail | null | undefined,
  citationId: string | null | undefined,
  isScreenshotDoc: (documentId: string | null | undefined) => boolean,
): TrailDisplayItem | null {
  if (!trail || !citationId) return null;
  for (const step of buildTrailDisplay(trail, undefined, isScreenshotDoc)) {
    for (const item of step.items) {
      if (item.source.id === citationId || item.screenshot?.id === citationId) {
        return item;
      }
    }
  }
  return null;
}
